import { type ReactNode, useEffect, useRef } from "react";

export interface EditorIssue {
  readonly key: string;
  readonly message: string;
  /** The input the summary jumps to; absent for whole-section issues. */
  readonly inputId?: string;
}

/**
 * The frame every profile section editor renders inside: the validation
 * summary (an alert that takes focus and jumps to the offending field), the
 * fields, and one primary action. Saving disables the fieldset so nothing
 * changes under an in-flight write.
 */
export function SectionEditorFrame({
  label,
  issues,
  serverError,
  saving,
  dirty,
  onSubmit,
  onCancel,
  onReload,
  children,
}: {
  readonly label: string;
  readonly issues: readonly EditorIssue[];
  readonly serverError: string | null;
  readonly saving: boolean;
  readonly dirty: boolean;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  readonly onReload?: (() => void) | undefined;
  readonly children: ReactNode;
}) {
  const alertRef = useRef<HTMLDivElement>(null);
  const showAlert = issues.length > 0 || serverError !== null;
  useEffect(() => {
    if (showAlert) alertRef.current?.focus();
  }, [showAlert]);

  return (
    <form
      className="pp-form pp-editor"
      aria-label={`Edit ${label.toLowerCase()}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!saving && onReload === undefined) onSubmit();
      }}
    >
      {showAlert ? (
        <div ref={alertRef} tabIndex={-1} className="pp-error-block" role="alert">
          <p>{serverError ?? "Fix the highlighted fields before saving."}</p>
          {issues.length > 0 ? (
            <ul className="pp-issue-list">
              {issues.map((issue) => (
                <li key={issue.key}>
                  {issue.inputId ? (
                    <button
                      type="button"
                      className="pp-link"
                      onClick={() => document.getElementById(issue.inputId ?? "")?.focus()}
                    >
                      {issue.message}
                    </button>
                  ) : (
                    issue.message
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <fieldset className="pp-fieldset" disabled={saving}>
        {children}
      </fieldset>
      <div className="pp-editor-actions">
        <button
          type="submit"
          className="pp-btn primary sm"
          disabled={saving || onReload !== undefined}
        >
          {saving ? (onReload === undefined ? "Saving…" : "Reloading…") : "Save"}
        </button>
        {onReload !== undefined ? (
          <button type="button" className="pp-btn primary sm" onClick={onReload} disabled={saving}>
            Reload latest section
          </button>
        ) : null}
        <button type="button" className="pp-btn ghost sm" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        {dirty && !saving ? <span className="pp-quiet">Unsaved changes</span> : null}
      </div>
    </form>
  );
}
